import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Save, Upload } from "lucide-react";

const PAGES = [
  ["home", "Ana Sayfa"], ["about", "Hakkımızda"], ["privacy", "Gizlilik"],
  ["distance", "Mesafeli Satış"], ["delivery", "Teslimat & İade"],
];

const CONTENT_GROUPS = [
  { title: "Hero (Ana Bölüm)", fields: [
    ["hero_eyebrow", "Üst Etiket", "input"], ["hero_title", "Başlık", "input"],
    ["hero_subtitle", "Alt Metin", "textarea"], ["hero_cta_primary", "Buton 1 Metni", "input"],
    ["hero_cta_secondary", "Buton 2 Metni", "input"], ["hero_image", "Hero Görseli", "media"],
  ]},
  { title: "CTA (Çağrı Bölümü)", fields: [
    ["cta_title", "Başlık", "input"], ["cta_subtitle", "Alt Metin", "textarea"], ["cta_image", "Görsel", "media"],
  ]},
  { title: "İletişim (Footer)", fields: [
    ["footer_email", "E-posta", "input"], ["footer_phone", "Telefon", "input"], ["footer_address", "Adres", "input"],
  ]},
];

export default function AdminContent() {
  const navigate = useNavigate();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const fileRefs = useRef({});

  useEffect(() => {
    api.get("/content").then((r) => setData(r.data || {})).finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const onUpload = async (key, file) => {
    if (!file) return;
    setUploading(key);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set(key, r.data.url);
      toast.success("Dosya yüklendi");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setUploading(""); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/content", data);
      setData(r.data);
      toast.success("Kaydedildi. (Canlıda görünmesi için deploy gerekmez, içerik anında yansır.)");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  if (loading) return <div className="max-w-3xl mx-auto px-6 py-16 text-white/40 animate-pulse">Yükleniyor…</div>;

  const renderField = ([key, label, type]) => (
    <div key={key}>
      <Label className="text-white/70">{label}</Label>
      {type === "textarea" ? (
        <Textarea value={data[key] || ""} onChange={(e) => set(key, e.target.value)} rows={3}
          data-testid={`content-${key}`} className="mt-2 bg-[#121212] border-white/10 focus:border-gold resize-none" />
      ) : type === "media" ? (
        <div className="mt-2">
          <div className="flex gap-2">
            <Input value={data[key] || ""} onChange={(e) => set(key, e.target.value)}
              data-testid={`content-${key}`} placeholder="URL veya bilgisayardan yükle"
              className="bg-[#121212] border-white/10 focus:border-gold h-11" />
            <input type="file" accept="image/*,video/*" hidden
              ref={(el) => (fileRefs.current[key] = el)}
              onChange={(e) => onUpload(key, e.target.files[0])} data-testid={`upload-input-${key}`} />
            <Button type="button" onClick={() => fileRefs.current[key]?.click()} disabled={uploading === key}
              data-testid={`upload-btn-${key}`}
              className="bg-gold text-black hover:bg-gold-light rounded-md h-11 px-4 shrink-0">
              <Upload className="w-4 h-4 mr-1" /> {uploading === key ? "…" : "Yükle"}
            </Button>
          </div>
          {data[key] && (data[key].match(/\.(mp4|webm|mov)/i)
            ? <video src={data[key]} controls className="mt-3 rounded-lg border border-white/10 h-40" />
            : <img src={data[key]} alt="önizleme" className="mt-3 rounded-lg border border-white/10 h-40 object-cover" />)}
        </div>
      ) : (
        <Input value={data[key] || ""} onChange={(e) => set(key, e.target.value)}
          data-testid={`content-${key}`} className="mt-2 bg-[#121212] border-white/10 focus:border-gold h-11" />
      )}
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto px-6 py-12" data-testid="admin-content-page">
      <button onClick={() => navigate("/admin")} data-testid="content-back-button"
        className="flex items-center gap-2 text-white/50 hover:text-gold transition-colors mb-8 text-sm">
        <ArrowLeft className="w-4 h-4" /> Yönetim paneline dön
      </button>
      <p className="text-sm uppercase tracking-[0.3em] text-gold mb-2">Stüdyo Yönetimi</p>
      <h1 className="font-serif text-4xl lg:text-5xl mb-10">Site İçeriği & SEO</h1>

      <div className="space-y-8">
        {CONTENT_GROUPS.map((g) => (
          <section key={g.title} className="bg-[#0A0A0A] border border-white/5 rounded-xl p-6">
            <h2 className="font-serif text-2xl mb-5 text-gold">{g.title}</h2>
            <div className="space-y-5">{g.fields.map(renderField)}</div>
          </section>
        ))}

        <section className="bg-[#0A0A0A] border border-white/5 rounded-xl p-6">
          <h2 className="font-serif text-2xl mb-5 text-gold">Sayfa Bazlı SEO</h2>
          <div className="space-y-8">
            {PAGES.map(([p, label]) => (
              <div key={p} className="border-t border-white/5 pt-5 first:border-0 first:pt-0">
                <p className="text-xs uppercase tracking-widest text-white/50 mb-3">{label}</p>
                <div className="space-y-4">
                  {renderField([`seo_${p}_title`, "Sayfa Başlığı (title)", "input"])}
                  {renderField([`seo_${p}_description`, "Meta Açıklama", "textarea"])}
                  {renderField([`seo_${p}_keywords`, "Anahtar Kelimeler (virgülle)", "input"])}
                  {renderField([`seo_${p}_og`, "Sosyal Önizleme Görseli (OG)", "media"])}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="sticky bottom-6 mt-8">
        <Button onClick={save} disabled={saving} data-testid="content-save-button"
          className="bg-gold text-black hover:bg-gold-light rounded-md h-12 px-8 shadow-lg">
          <Save className="w-4 h-4 mr-2" /> {saving ? "Kaydediliyor…" : "Değişiklikleri Kaydet"}
        </Button>
      </div>
    </div>
  );
}
